import React from 'react';
import { LinksData } from '../../../prompt/preview/components/stats/models/StatsModel';
import { getMonitorTypesForChatType } from '../../../../shared/helpers/MonitorTypeHelpers';
import { ChatType, getChatTypeToName } from '../../../../shared/types/monitor/ChatType';
import { useTranslation } from 'react-i18next';
import { formatLargeNumber } from '../../../../shared/utils/utils';
import { getChatTypeIcon } from '../../../../shared/helpers/ChatTypeHelpers';

interface CitationsBoxProps {
  chatType: ChatType;
  linksData: LinksData[];
  // When set, the box aggregates across these chat types (the "All" tile) instead of `chatType`.
  aggregateChatTypes?: ChatType[];
}

const totalForChatType = (linksData: LinksData[], chatType: ChatType): number =>
  linksData
    .filter((linkData) =>
      linkData.models.some((model) => getMonitorTypesForChatType(chatType).includes(model))
    )
    .reduce((sum, linkData) => sum + linkData.count / linkData.models.length, 0);

const pagesForChatType = (linksData: LinksData[], chatType: ChatType): number =>
  linksData.filter((linkData) =>
    linkData.models.some((model) => getMonitorTypesForChatType(chatType).includes(model))
  ).length;

const CitationsBox: React.FC<CitationsBoxProps> = ({ chatType, linksData, aggregateChatTypes }) => {
  const { t } = useTranslation();
  const icon = getChatTypeIcon(chatType);

  let total: number;
  let pages: number;
  if (aggregateChatTypes) {
    // Sum of the per-model tiles, so "All" === tile1 + tile2 + …
    total = aggregateChatTypes.reduce((sum, ct) => sum + totalForChatType(linksData, ct), 0);
    // Same summing for pages: sum of each model's cited-page count (a page cited in N models counts N times).
    pages = aggregateChatTypes.reduce((sum, ct) => sum + pagesForChatType(linksData, ct), 0);
  } else {
    total = totalForChatType(linksData, chatType);
    pages = pagesForChatType(linksData, chatType);
  }

  return (
    <div className="citations-box">
      <div className="citations-box__header">
        {icon && <span className={`${icon} ico--md`} />}
        <div className="citations-box__title">{getChatTypeToName(chatType)}</div>
      </div>
      <div className="citations-box__row">
        <div className="citations-box__value-box">
          <span className="citations-box__value">{formatLargeNumber(Math.round(total))}</span>
        </div>
      </div>
      <div className="citations-box__row">
        {t('pages')}: {formatLargeNumber(pages)}
      </div>
    </div>
  );
};

export default CitationsBox;
